/**
 * Permission codes the Manufacturing screens declare an interest in.
 *
 * These are the dot-strings `internal/mfg/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`.
 *
 * **Three segregation-of-duties pairs run through this module and no screen
 * infers one half from the other:**
 *
 *  - `orderRelease` / `orderClose` — releasing commits a material reservation
 *    and (once material is issued) a WIP posting; closing computes and posts
 *    every variance and can sweep a residual straight to a variance account.
 *  - `materialIssue` / `fgReceive` — whoever puts components into WIP is not
 *    whoever declares finished output complete.
 *  - `qcInspect` / `ncrResolve` — an inspector does not decide the disposition
 *    of their own failed inspection.
 *
 * So the action bar renders only the steps the user actually holds, and never a
 * disabled row of buttons implying somebody is one grant away from doing both
 * halves of a control.
 *
 * `settingsRead` is separate from `settingsManage` for the reason the server
 * gives: the WIP and variance accounts are the rule production staff are judged
 * against, so the people held to it can see it without holding the right to
 * change it.
 *
 * Four codes belong to other modules and are listed because these screens
 * cannot do their job without them:
 *
 *  - `companyRead` — everything here is company-scoped, and the company's base
 *    currency is what a WIP balance and a variance are stated in.
 *  - `itemRead` — a BOM line, a routing, an order and an MRP suggestion all
 *    name an item by id; without inventory's catalogue the screens could only
 *    show numbers.
 *  - `warehouseRead` — an order consumes and receives stock in one warehouse.
 *  - `accountRead` — the settings screen picks the WIP, applied-cost and five
 *    variance accounts from the company's own chart.
 *  - `journalRead` — every posting this module makes drills through to its
 *    journal entry.
 */

export const MFG_PERMISSIONS = {
  workCenterRead: 'mfg.workcenter.read',
  workCenterManage: 'mfg.workcenter.manage',

  bomRead: 'mfg.bom.read',
  bomManage: 'mfg.bom.manage',
  bomActivate: 'mfg.bom.activate',

  routingRead: 'mfg.routing.read',
  routingManage: 'mfg.routing.manage',
  routingActivate: 'mfg.routing.activate',

  standardCostRead: 'mfg.standard_cost.read',
  standardCostManage: 'mfg.standard_cost.manage',

  orderRead: 'mfg.production_order.read',
  orderCreate: 'mfg.production_order.create',
  orderUpdate: 'mfg.production_order.update',
  /** SoD partner of `orderClose`. */
  orderRelease: 'mfg.production_order.release',
  /** SoD partner of `orderRelease`. Posts every variance. */
  orderClose: 'mfg.production_order.close',
  orderCancel: 'mfg.production_order.cancel',

  /** SoD partner of `fgReceive`. */
  materialIssue: 'mfg.material.issue',
  /** SoD partner of `materialIssue`. */
  fgReceive: 'mfg.fg.receive',

  operationLog: 'mfg.operation.log',

  /** SoD partner of `ncrResolve`. */
  qcInspect: 'mfg.qc.inspect',
  /** SoD partner of `qcInspect`. */
  ncrResolve: 'mfg.ncr.resolve',

  varianceRead: 'mfg.variance.read',

  mrpRun: 'mfg.mrp.run',
  mrpRead: 'mfg.mrp.read',
  /** Both dismissing and actioning a suggestion, per the server's own routes. */
  mrpDismiss: 'mfg.mrp.dismiss',

  reportRead: 'mfg.report.read',

  settingsRead: 'mfg.settings.read',
  settingsManage: 'mfg.settings.manage',

  /* ------------------------------------------------------------- C7 (§6.1) */

  ecoRead: 'mfg.eco.read',
  /** SoD-adjacent to `ecoApprove`: drafting a change is not approving it. */
  ecoManage: 'mfg.eco.manage',
  ecoApprove: 'mfg.eco.approve',

  /**
   * Substituting a material at issue.
   *
   * SoD partner of `orderClose`, and for the same reason `materialIssue` already
   * is: substituting creates the variance and closing plugs the residual.
   */
  materialSubstitute: 'mfg.material.substitute',

  capacityRead: 'mfg.capacity.read',
  capacityManage: 'mfg.capacity.manage',
  /** Running the finite scheduler, and re-sequencing a dispatch list. */
  scheduleRun: 'mfg.schedule.run',

  planningManage: 'mfg.planning.manage',

  /**
   * Computing a settlement preview.
   *
   * Read-shaped, and deliberately **not** the same as closing: a supervisor may
   * see what an order would settle to without being the person who posts it.
   */
  settlementPreview: 'mfg.settlement.preview',

  /** Step-up MFA. Computing and applying a standard cost roll-up. */
  standardCostRollup: 'mfg.standard_cost.rollup',

  toolManage: 'mfg.tool.manage',

  /* ------------------------------------------------- other modules' codes */

  companyRead: 'org.company.read',
  accountRead: 'org.account.read',
  itemRead: 'inventory.item.read',
  warehouseRead: 'inventory.warehouse.read',
  journalRead: 'gl.journal.read',
} as const

export type MfgPermission = (typeof MFG_PERMISSIONS)[keyof typeof MFG_PERMISSIONS]
